import React, { useMemo } from 'react';
import { Button, Collapse, Form } from 'react-bootstrap';
import Icon from '../../common/Icons';
import ReportsFilterBar from '../../head-doctor/reports/ReportsFilterBar';
import {
  MAJOR_REPORTS_AGE_PRESETS,
  MAJOR_REPORTS_MEMBER_TYPES,
  MAJOR_REPORTS_SEXES,
  countActiveMajorReportFilters,
  countAgeFilters
} from './majorReportsFilters';

export default function MajorReportsFilterBar({
  copy,
  from,
  to,
  siteFilter,
  workSites,
  loading,
  activePreset,
  tabScoped,
  tabScopeLabel,
  advancedOpen,
  advancedFilters,
  companies = [],
  activeAgePreset,
  onFromChange,
  onToChange,
  onSiteChange,
  onPreset,
  onRefresh,
  onAdvancedOpenChange,
  onAdvancedChange,
  onAgePreset,
  onResetAdvanced
}) {
  const activeCount = useMemo(
    () => countActiveMajorReportFilters(advancedFilters),
    [advancedFilters]
  );
  const ageFilterCount = useMemo(() => countAgeFilters(advancedFilters), [advancedFilters]);

  return (
    <div className="major-reports-filters">
      <ReportsFilterBar
        copy={copy}
        allowAllSites={true}
        showSiteFilter={workSites.length > 0}
        from={from}
        to={to}
        siteFilter={siteFilter}
        workSites={workSites}
        loading={loading}
        activePreset={activePreset}
        onFromChange={onFromChange}
        onToChange={onToChange}
        onSiteChange={onSiteChange}
        onPreset={onPreset}
        onRefresh={onRefresh}
      />

      {tabScoped && tabScopeLabel ? (
        <div className="major-reports-filters__tab-scope" role="status">
          <Icon name="chartBar" size={14} aria-hidden />
          <span>{copy.FILTER_TAB_SCOPE_PREFIX}</span>
          <strong>{tabScopeLabel}</strong>
        </div>
      ) : null}

      <div className="major-reports-filters__primary" aria-label={copy.FILTER_PRIMARY_ARIA}>
        <label className="major-reports-filters__field major-reports-filters__field--search">
          <span className="major-reports-filters__label">{copy.FILTER_PATIENT_SEARCH}</span>
          <Form.Control
            size="sm"
            value={advancedFilters.q}
            onChange={(e) => onAdvancedChange('q', e.target.value)}
            placeholder={copy.FILTER_PATIENT_SEARCH_PH}
            disabled={loading}
          />
        </label>

        <label className="major-reports-filters__field">
          <span className="major-reports-filters__label">{copy.FILTER_COMPANY}</span>
          <Form.Select
            size="sm"
            value={advancedFilters.company_id}
            onChange={(e) => onAdvancedChange('company_id', e.target.value)}
            disabled={loading}
          >
            <option value="">{copy.FILTER_COMPANY_ALL}</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </Form.Select>
        </label>

        <label className="major-reports-filters__field">
          <span className="major-reports-filters__label">{copy.FILTER_MEMBER_TYPE}</span>
          <Form.Select
            size="sm"
            value={advancedFilters.member_type}
            onChange={(e) => onAdvancedChange('member_type', e.target.value)}
            disabled={loading}
          >
            {MAJOR_REPORTS_MEMBER_TYPES.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {copy[option.labelKey]}
              </option>
            ))}
          </Form.Select>
        </label>

        <label className="major-reports-filters__field">
          <span className="major-reports-filters__label">{copy.FILTER_SEX}</span>
          <Form.Select
            size="sm"
            value={advancedFilters.sexe}
            onChange={(e) => onAdvancedChange('sexe', e.target.value)}
            disabled={loading}
          >
            {MAJOR_REPORTS_SEXES.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {copy[option.labelKey]}
              </option>
            ))}
          </Form.Select>
        </label>

        <label className="major-reports-filters__field major-reports-filters__field--search">
          <span className="major-reports-filters__label">{copy.FILTER_ACT_SEARCH}</span>
          <Form.Control
            size="sm"
            value={advancedFilters.act_q}
            onChange={(e) => onAdvancedChange('act_q', e.target.value)}
            placeholder={copy.FILTER_ACT_SEARCH_PH}
            disabled={loading}
          />
        </label>
      </div>

      <div className="major-reports-filters__secondary-toggle">
        <Button
          variant="outline-secondary"
          size="sm"
          className="major-reports-filters__toggle-btn"
          onClick={() => onAdvancedOpenChange(!advancedOpen)}
          aria-expanded={advancedOpen}
        >
          <Icon name="calendar" size={14} className="me-1" aria-hidden />
          {copy.FILTER_AGE_TOGGLE}
          {ageFilterCount > 0 ? (
            <span className="major-reports-filters__badge">{ageFilterCount}</span>
          ) : null}
        </Button>
        {activeCount > 0 ? (
          <Button variant="link" size="sm" className="major-reports-filters__reset" onClick={onResetAdvanced}>
            {copy.FILTER_RESET}
          </Button>
        ) : null}
      </div>

      <Collapse in={advancedOpen}>
        <div className="major-reports-filters__panel">
          <div className="major-reports-filters__age-row">
            <label className="major-reports-filters__field">
              <span className="major-reports-filters__label">{copy.FILTER_AGE_MIN}</span>
              <Form.Control
                size="sm"
                type="number"
                min={0}
                max={120}
                value={advancedFilters.age_min}
                onChange={(e) => onAdvancedChange('age_min', e.target.value)}
                placeholder="—"
                disabled={loading}
              />
            </label>
            <label className="major-reports-filters__field">
              <span className="major-reports-filters__label">{copy.FILTER_AGE_MAX}</span>
              <Form.Control
                size="sm"
                type="number"
                min={0}
                max={120}
                value={advancedFilters.age_max}
                onChange={(e) => onAdvancedChange('age_max', e.target.value)}
                placeholder="—"
                disabled={loading}
              />
            </label>
          </div>

          <div className="major-reports-filters__presets" role="group" aria-label={copy.FILTER_AGE_PRESETS_ARIA}>
            <span className="major-reports-filters__presets-label">{copy.FILTER_AGE_PRESETS}</span>
            {MAJOR_REPORTS_AGE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`major-reports-filters__age-preset${
                  activeAgePreset === preset.key ? ' is-active' : ''
                }`}
                onClick={() => onAgePreset(preset)}
                disabled={loading}
              >
                {copy[preset.labelKey]}
              </button>
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
}
